import { useMemo, useState } from "react";

import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";

import StatusChip from "./StatusChip";

import { PreventiveMaintenance } from "../types";

interface PreventiveMaintenanceTableProps {
  data: PreventiveMaintenance[];
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (pageSize: number) => void;
  onCreateInHouseRepair: (maintenance: PreventiveMaintenance) => void;
  onViewInHouseTicket: (maintenance: PreventiveMaintenance) => void;
  hubNames: Record<string, string>;
}

const formatDate = (date?: string) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });
};

const calculateRemainingDays = (nextDate: string) => {
  const istDay = (date: number) => Math.floor((date + 330 * 60_000) / 86_400_000);
  return istDay(new Date(nextDate).getTime()) - istDay(Date.now());
};

const PreventiveMaintenanceTable = ({
  data,
  page,
  pageSize,
  total,
  onPageChange,
  onRowsPerPageChange,
  onCreateInHouseRepair,
  onViewInHouseTicket,
  hubNames,
}: PreventiveMaintenanceTableProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] =
    useState<PreventiveMaintenance | null>(null);

  const rows = useMemo(() => data, [data]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: PreventiveMaintenance
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Schedule</strong>
              </TableCell>
              <TableCell>
                <strong>Vehicle</strong>
              </TableCell>
              <TableCell>
                <strong>Rider ID</strong>
              </TableCell>
              <TableCell>
                <strong>Hub</strong>
              </TableCell>
              <TableCell>
                <strong>Deployed</strong>
              </TableCell>
              <TableCell>
                <strong>Last Service</strong>
              </TableCell>
              <TableCell>
                <strong>Next Service</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Interval</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Days Left</strong>
              </TableCell>
              <TableCell>
                <strong>Last Ticket</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Status</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    No preventive maintenance schedules found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const remainingDays = calculateRemainingDays(row.nextServiceDate);

                return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Box>
                        <Typography fontWeight={700}>
                          {row.maintenanceNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.recordStatus}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography fontWeight={600}>{row.vehicleNumber}</Typography>
                    </TableCell>

                    <TableCell sx={{ overflowWrap: "anywhere", maxWidth: 180 }}>{row.riderDisplayId || "-"}</TableCell>
                    <TableCell>{hubNames[row.warehouseId || ""] || "-"}</TableCell>
                    <TableCell>{formatDate(row.deploymentDate || undefined)}</TableCell>
                    <TableCell>{row.deploymentDate === row.lastServiceDate ? "-" : formatDate(row.lastServiceDate)}</TableCell>
                    <TableCell>{formatDate(row.nextServiceDate)}</TableCell>

                    <TableCell align="center">
                      <Chip
                        label={`${row.intervalDays} Days`}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={
                          remainingDays >= 0
                            ? `${remainingDays} Days`
                            : `${Math.abs(remainingDays)} Overdue`
                        }
                        color={
                          remainingDays < 0
                            ? "error"
                            : remainingDays <= 7
                            ? "warning"
                            : "success"
                        }
                      />
                    </TableCell>

                    <TableCell>
                      {row.lastTicketCode || row.generatedTicket?.code || "-"}
                    </TableCell>

                    <TableCell align="center">
                      <StatusChip status={row.status} />
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title="More Actions">
                        <IconButton onClick={(event) => handleMenuOpen(event, row)}>
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {selectedRow?.generatedTicket ? (
          <MenuItem
            onClick={() => {
              if (selectedRow) onViewInHouseTicket(selectedRow);
              handleMenuClose();
            }}
          >
            <OpenInNewRoundedIcon fontSize="small" sx={{ mr: 1 }} />
            View In-House Ticket
          </MenuItem>
        ) : (
          <MenuItem
            disabled={
              !selectedRow ||
              !["DUE_TODAY", "OVERDUE"].includes(selectedRow.status)
            }
            onClick={() => {
              if (selectedRow) onCreateInHouseRepair(selectedRow);
              handleMenuClose();
            }}
          >
            <BuildRoundedIcon fontSize="small" sx={{ mr: 1 }} />
            Create In-House Repair
          </MenuItem>
        )}

      </Menu>

      <TablePagination
        component="div"
        count={total}
        page={Math.max(0, page - 1)}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        onRowsPerPageChange={(event) =>
          onRowsPerPageChange(Number(event.target.value))
        }
      />
    </Paper>
  );
};

export default PreventiveMaintenanceTable;
